/**
 * Full-screen category vote overlay.
 *
 * Replaces the old Three.js poll-overlay for the `phase=category` case.
 * Shows a clean 3×2 grid of vote cards centered in the stage. Board is
 * hidden underneath (the scene-visibility gate keeps it off when a
 * category poll is open), so viewers see only the six options while
 * they're voting — maximum clarity, no visual mash-up with the board
 * tiles behind.
 *
 * Cards use Oswald for the category label, a subtle dim border by
 * default, and a bright `--sg-primary` border with glow on the currently
 * leading option. Top-left of each card carries a large circular badge
 * showing the 1–6 selection number (yellow bg, deep-blue text) so viewers
 * can instantly see which number to type. Bottom-right carries a sizeable
 * vote count chip with a "VOTES" label so the leading option is obvious
 * at a glance. fitText keeps long category names from overflowing around
 * those two fixed badges.
 *
 * Lifecycle: created once at boot; open()/close() toggles visibility +
 * rebuilds the cards for a fresh poll. setTallies() updates the count
 * chips + leader styling on each tally_update frame.
 */

import type { PollOption } from '@sg/shared';
import { fitText, unfitText } from './fit-text.js';

export interface CategoryVoteHandle {
  el: HTMLDivElement;
  open: (options: PollOption[]) => void;
  close: () => void;
  setTallies: (tallies: Record<string, number>) => void;
  setWinner: (key: string | null) => void;
  dispose: () => void;
}

interface CardState {
  key: string;
  card: HTMLDivElement;
  label: HTMLDivElement;
  chip: HTMLDivElement;
  chipCount: HTMLSpanElement;
  index: number;
  used: boolean;
}

const ROOT_ID = 'sg-category-vote';

const STYLES = `
  @keyframes sg-catvote-in {
    from { opacity: 0; transform: translateY(16px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }
  @keyframes sg-catvote-leader {
    0%, 100% { box-shadow: 0 0 0 rgba(255,196,61,0.0), 0 12px 32px rgba(0,0,0,0.5); }
    50%      { box-shadow: 0 0 32px rgba(255,196,61,0.55), 0 12px 32px rgba(0,0,0,0.5); }
  }
  .sg-catvote {
    position: absolute;
    /* Left-of-center container so the grid doesn't overlap the permanent
       right-side leaderboard (which sits at stage-x=1500..1880). No scrim
       background: the board is hidden during poll_category (see
       scene-visibility.ts) so the overlay has nothing to cover up, and the
       radial scrim used to end at right:420px creating a sharp vertical
       edge where the leaderboard begins. Cards are bold enough against
       the stage background on their own. */
    left: 0;
    top: 0;
    /* Right offset bumped 420 -> 540 (Apr 2026) so the grid's centre lands
       ~60px left of the previous position, matching the board's leftward
       shift (-280 -> -370). Keeps the visual story coherent: board, category
       vote, and value vote all sit centred in the LEFT 1180px content area
       rather than the full stage's 1920px geometric centre. */
    right: 540px;
    bottom: 0;
    z-index: 40;
    display: none;
    align-items: center;
    justify-content: center;
    background: transparent;
    pointer-events: none;
  }
  .sg-catvote.visible { display: flex; }
  .sg-catvote-grid {
    display: grid;
    /* Default 6-option layout (classic): 3x2 grid. */
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 24px;
    /* Trimmed from 1280→1180 (Apr 2026) so the grid + card scale(1.04)
       leader transform never spills past x=1500 where the right-side
       leaderboard panel begins. The container already has right:420
       framing the live area, but cards animating to scale(1.04) added a
       few px of bleed visible as a hairline overlap. */
    width: 1180px;
    max-width: 1180px;
    height: 620px;
    padding: 20px;
  }
  /* 3-option mode (streamer toggle, Apr 2026): single 1x3 row, full-height
     cards. Same overall envelope so the surrounding stage layout doesn't
     shift; cards just become taller + wider for the chunky board feel. */
  .sg-catvote-grid.cols-3 {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr;
  }
  /* Belt-and-braces: when 6 options are shown ensure the 3x2 layout sticks. */
  .sg-catvote-grid.cols-6 {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }
  .sg-catvote-card {
    position: relative;
    background: linear-gradient(180deg, rgba(6,12,233,0.92) 0%, rgba(3,5,55,0.95) 100%);
    border: 3px solid rgba(255,196,61,0.3);
    border-radius: 14px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 32px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.5);
    animation: sg-catvote-in 320ms cubic-bezier(.22,.9,.36,1) backwards;
    transition: border-color 180ms ease-out, transform 180ms ease-out;
  }
  .sg-catvote-card.leader {
    border-color: var(--sg-primary, #FFC43D);
    animation: sg-catvote-in 320ms cubic-bezier(.22,.9,.36,1) backwards,
               sg-catvote-leader 1.8s ease-in-out infinite 320ms;
    transform: scale(1.02);
  }
  .sg-catvote-card.winner {
    border-color: var(--sg-primary, #FFC43D);
    background: linear-gradient(180deg, rgba(255,196,61,0.25) 0%, rgba(6,12,233,0.95) 100%);
    transform: scale(1.04);
  }
  .sg-catvote-card.used {
    /* Mirror value-vote: spent column slots stay visible (badge keeps its
       slot number) but render dim so leader/winner glow can't fire. */
    opacity: 0.32;
    filter: grayscale(0.4);
    border-color: rgba(255,255,255,0.18);
    box-shadow: none;
  }
  .sg-catvote-card.used .sg-catvote-chip {
    display: none;
  }
  .sg-catvote-label {
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.05;
    color: #fff;
    text-shadow: 0 3px 10px rgba(0,0,0,0.6), 0 0 18px rgba(255,196,61,0.25);
    /* Inset the label so fitText measures against a box that clears the
       top-left number badge + bottom-right vote chip. Keeps long names
       (GEOGRAPHY, HISTORY) from colliding with the enlarged badges. */
    width: 100%;
    height: 100%;
    padding: 16px 8px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  /* Big circular badge in the top-left corner: viewers should spot the
     number they need to type at a glance, so this reads as a clear
     secondary element under the category label. Brand yellow bg + deep
     blue text keeps it on-brand and highly legible. */
  .sg-catvote-index {
    position: absolute;
    top: 14px;
    left: 14px;
    width: 88px;
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    font-size: 64px;
    font-weight: 700;
    line-height: 1;
    color: var(--sg-text-on-primary, #060CE9);
    background: var(--sg-primary, #FFC43D);
    border-radius: 50%;
    box-shadow: 0 6px 16px rgba(0,0,0,0.5), 0 0 0 3px rgba(3,5,55,0.9);
    text-shadow: none;
  }
  /* Vote count chip: clearly readable tertiary element in the bottom-right.
     Larger type + explicit "VOTES" label so viewers can instantly see which
     option is leading without squinting. */
  .sg-catvote-chip {
    position: absolute;
    bottom: 14px;
    right: 14px;
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    font-weight: 700;
    font-size: 40px;
    line-height: 1;
    color: var(--sg-primary, #FFC43D);
    background: rgba(0,0,0,0.55);
    border: 2px solid rgba(255,196,61,0.55);
    padding: 8px 18px 6px 18px;
    border-radius: 14px;
    min-width: 72px;
    text-align: center;
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
    letter-spacing: 0.04em;
  }
  .sg-catvote-chip-label {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.78);
  }
  .sg-catvote-card.leader .sg-catvote-chip {
    background: var(--sg-primary, #FFC43D);
    color: var(--sg-text-on-primary, #060CE9);
    border-color: var(--sg-primary, #FFC43D);
  }
  .sg-catvote-card.leader .sg-catvote-chip-label {
    color: rgba(6,12,233,0.75);
  }
`;

/**
 * Mount the category-vote overlay inside `stageEl` (the 1920×1080 DOM
 * stage) so the CSS transform scales with the rest of the UI.
 */
export function createCategoryVote(stageEl: HTMLElement): CategoryVoteHandle {
  // Inject styles once per document. Idempotent across HMR.
  if (!document.getElementById(`${ROOT_ID}-styles`)) {
    const style = document.createElement('style');
    style.id = `${ROOT_ID}-styles`;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  const el = document.createElement('div');
  el.id = ROOT_ID;
  el.className = 'sg-catvote';
  el.setAttribute('data-testid', 'category-vote');
  stageEl.appendChild(el);

  const grid = document.createElement('div');
  grid.className = 'sg-catvote-grid';
  el.appendChild(grid);

  let cards: CardState[] = [];
  let winner: string | null = null;
  let tallies: Record<string, number> = {};

  function clearCards(): void {
    for (const c of cards) unfitText(c.label);
    grid.innerHTML = '';
    cards = [];
  }

  function renderCard(opt: PollOption, i: number): CardState {
    const card = document.createElement('div');
    card.className = 'sg-catvote-card';
    if (opt.used) card.classList.add('used');
    card.style.animationDelay = `${i * 60}ms`;

    const idx = document.createElement('div');
    idx.className = 'sg-catvote-index';
    idx.textContent = String(i + 1);
    card.appendChild(idx);

    const label = document.createElement('div');
    label.className = 'sg-catvote-label';
    label.textContent = opt.label.toUpperCase();
    card.appendChild(label);

    const chip = document.createElement('div');
    chip.className = 'sg-catvote-chip';
    const chipCount = document.createElement('span');
    chipCount.className = 'sg-catvote-chip-count';
    chipCount.textContent = '0';
    const chipLabel = document.createElement('span');
    chipLabel.className = 'sg-catvote-chip-label';
    chipLabel.textContent = 'VOTES';
    chip.appendChild(chipCount);
    chip.appendChild(chipLabel);
    card.appendChild(chip);

    grid.appendChild(card);

    // Fit the label inside the card — defer one frame so layout is ready.
    // Max cap trimmed so the number badge + vote chip don't collide with
    // the centered category name on short labels (e.g. "MOVIES").
    requestAnimationFrame(() => {
      fitText(label, { min: 22, max: 72, lineHeight: 1.05 });
    });

    return { key: opt.key, card, label, chip, chipCount, index: i, used: opt.used === true };
  }

  function open(options: PollOption[]): void {
    clearCards();
    winner = null;
    tallies = {};
    // Pick the right grid layout class for the option count. The streamer
    // toggle (categories_per_round) drives this — backend ships either 3 or
    // 6 options per category poll. Anything else (corpus too small, edge
    // case) falls through to the default 3-col CSS rule above.
    grid.classList.remove('cols-3', 'cols-6');
    if (options.length <= 3) grid.classList.add('cols-3');
    else if (options.length === 6) grid.classList.add('cols-6');
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      if (opt) cards.push(renderCard(opt, i));
    }
    el.classList.add('visible');
    applyLeaderStyling();
  }

  function close(): void {
    el.classList.remove('visible');
    // Defer clearing DOM so any in-flight animations finish cleanly.
    setTimeout(() => {
      if (!el.classList.contains('visible')) {
        clearCards();
        winner = null;
        tallies = {};
      }
    }, 400);
  }

  function setTallies(next: Record<string, number>): void {
    tallies = next;
    for (const c of cards) {
      const v = next[c.key] ?? 0;
      c.chipCount.textContent = String(v);
    }
    applyLeaderStyling();
  }

  function setWinner(key: string | null): void {
    winner = key;
    applyLeaderStyling();
  }

  function applyLeaderStyling(): void {
    // Find highest tally (>0). Ties: all tied cards share leader styling.
    // When a winner is set, that card gets the .winner class instead.
    // Used slots can never lead/win — they're skipped.
    let top = 0;
    for (const c of cards) {
      if (c.used) continue;
      const v = tallies[c.key] ?? 0;
      if (v > top) top = v;
    }
    for (const c of cards) {
      c.card.classList.remove('leader', 'winner');
      if (c.used) continue;
      if (winner !== null) {
        if (c.key === winner) c.card.classList.add('winner');
      } else if (top > 0 && (tallies[c.key] ?? 0) === top) {
        c.card.classList.add('leader');
      }
    }
  }

  return {
    el,
    open,
    close,
    setTallies,
    setWinner,
    dispose(): void {
      clearCards();
      el.remove();
    },
  };
}
